import { useEffect, useMemo, useRef, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Loader2, Play, RotateCcw } from "lucide-react";
import AvatarCanvasIdle from "../components/avatar-canvas-idle";
import VisemePlayer, {
  MappedVisemeEvent,
  VisemeDebugSnapshot,
  VisemePlaybackClock,
  VisemeVisualProfile,
} from "../components/viseme-player";
import { Button } from "../components/ui/button";
import { Textarea } from "../components/ui/textarea";
import { Avatar } from "../types";
import {
  CanonicalPoseImageUrls,
  EyePosition,
  fetchCanonicalPoseImageUrls,
  fetchEyePosition,
  resolveInterviewAvatarAssets,
} from "../lib/avatarAssets";
import { loadCanvasImage } from "../lib/canvasImage";
import { preloadImageInfo } from "../lib/imagePreload";
import { apiRequest } from "../lib/queryClient";

const PATIENT_JAW_MOTION = {
  transformOrigin: "50% 61%",
  maxTranslateYPercent: 1.18,
  maxScaleYDelta: 0.012,
  maxScaleXDelta: 0,
  transitionMs: 55,
} as const;

const PATIENT_SPEAKING_EYE_MOTION = {
  maskPosition: "35.8% 36.8%",
  maskSize: "24.25% 7.4%",
  maxOffsetXPercent: 0.05,
  maxOffsetYPercent: 0.03,
  firstBlinkMinMs: 750,
  firstBlinkMaxMs: 1350,
  intervalMinMs: 2600,
  intervalMaxMs: 4800,
  transitionMs: 22,
  centerReturnChance: 0.62,
} as const;

const DENTIST_JAW_MOTION = {
  transformOrigin: "50% 62%",
  maxTranslateYPercent: 2.8,
  maxScaleYDelta: 0,
  maxScaleXDelta: 0,
  transitionMs: 55,
} as const;

const MALE_DENTIST_FRONTBASE_JAW_MOTION = {
  ...DENTIST_JAW_MOTION,
  maxTranslateYPercent: 0.85,
  canvasMouthPatch: {
    x: 0.395,
    y: 0.525,
    width: 0.21,
    height: 0.145,
    maskCenterYRatio: 0.46,
    maskRadiusXRatio: 0.38,
    maskRadiusYRatio: 0.28,
    featherRatio: 0.035,
  },
} as const;

const DEBUG_CANVAS_WIDTH = 960;
const DEBUG_CANVAS_HEIGHT = 540;
const GENERATED_TRANSITION_FRAME_MAX_COUNT = 10;
const GENERATED_TRANSITION_PAIRS = [
  "REST_AA",
  "REST_E",
  "REST_I",
  "REST_U",
  "REST_O",
  "AA_E",
  "AA_I",
  "AA_U",
  "AA_O",
  "E_I",
  "E_U",
  "E_O",
  "I_U",
  "I_O",
  "U_O",
] as const;

const DEFAULT_DENTIST_EYE_POSITION: EyePosition = {
  top: "39.4%",
  left: "41.8%",
  width: "15.55%",
  height: "7.5%",
};

const DEBUG_FEMALE_20_34_RUNTIME_PATH =
  "patients/female_20_34/runtime_rich_mouthonly_i_filtered_20260430_165500";
const DEBUG_MALE_DENTIST_RUNTIME_PATH =
  "dentists/male_default/runtime_frontbase_champion_20260501_1850";

type DebugAvatarTarget = "female_20_34" | "male_dentist";

const DEBUG_AVATAR_TARGET_OPTIONS: Array<{
  key: DebugAvatarTarget;
  label: string;
}> = [
  {
    key: "female_20_34",
    label: "女性患者",
  },
  {
    key: "male_dentist",
    label: "男性歯科医",
  },
];

const VISUAL_PROFILE_OPTIONS: Array<{
  key: string;
  label: string;
  description: string;
  visualProfile: VisemeVisualProfile;
}> = [
  {
    key: "endingSettle",
    label: "I: チャンピオン",
    description: "Hに同期。話し終わりだけ早めにRESTへ寄せ、終了時の急な閉じを抑える。",
    visualProfile: "endingSettle",
  },
  {
    key: "generatedTransitions",
    label: "K: RIFE 60fps遷移",
    description: "専用transition画像があれば最大10枚を17ms刻みで間引く。無ければJにfallback。",
    visualProfile: "generatedTransitions",
  },
];

const TEST_PHRASES = [
  "あ。い。う。え。お。",
  "あい。いあ。いう。うい。",
  "おん。うん。えん。あん。",
  "あい、うい、おう、えい。",
  "こんにちは。担当の歯科医です。よろしくお願いします。",
  "お口を開けてください。奥歯を確認します。",
  "です。です。そうです。大丈夫です。",
  "よ、ろ、お、と、て、う、え。",
  "か、が、き、ぎ、く、ぐ、け、げ、こ、ご。",
  "かがく、かがみ、かくにん。きれいに動くか確認します。",
  "さ、ざ、す、ず、せ、ぜ、そ、ぞ。",
  "しせき、そうじ、すこしずつ確認します。",
  "し、ち、しゃ、しゅ、しょ、ちゃ、ちゅ、ちょ。",
  "歯科衛生士として、しっかり確認します。",
  "ら、り、る、れ、ろ。ふ、ふう、ふく、ふれる。",
  "リラックスして、ふつうにお話しください。",
  "よろしくお願いします。お口を閉じてください。",
  "とてもよくなっています。もう一度、よ、ろ、お、と。",
  "て、で、な、の、と。た、だ、な、の、と。",
];

interface DebugSampleResponse {
  text: string;
  contentType: string;
  audioBase64: string;
  visemes: MappedVisemeEvent[];
}

function resolveDebugTarget(): DebugAvatarTarget {
  const params = new URLSearchParams(window.location.search);
  const value = (params.get("avatar") || params.get("target") || params.get("profile") || "").toLowerCase();
  return value === "male_dentist" || value === "dentist" || value === "male" ? "male_dentist" : "female_20_34";
}

function shiftPercent(value: string, delta: number): string {
  if (!value.trim().endsWith("%")) return value;
  const numeric = Number.parseFloat(value);
  if (!Number.isFinite(numeric)) return value;
  return `${numeric + delta}%`;
}

function buildDentistSpeakingEyeMotion(eyePosition: EyePosition) {
  return {
    maskPosition: `${shiftPercent(eyePosition.left, -4)} ${shiftPercent(eyePosition.top, -1)}`,
    maskSize: `${shiftPercent(eyePosition.width, 8)} ${shiftPercent(eyePosition.height, 2)}`,
    maxOffsetXPercent: 0.05,
    maxOffsetYPercent: 0.03,
    firstBlinkMinMs: 750,
    firstBlinkMaxMs: 1350,
    intervalMinMs: 2600,
    intervalMaxMs: 4800,
    transitionMs: 22,
    centerReturnChance: 0.62,
  } as const;
}

function resolveDebugAvatar(target: DebugAvatarTarget, currentAvatar?: Avatar): Avatar {
  if (target === "male_dentist") {
    return {
      id: 0,
      name: "male_dentist viseme debug",
      age: 40,
      gender: "Male",
      type: "dentist",
      videoFiles: {
        baseVideo: "/videos/assets/dentists/male_default/base/idle.mp4",
        image: "/videos/assets/dentists/male_default/base/avatar.jpg",
        dubbingVideo: "/videos/assets/dentists/male_default/dubbing/dubbing.mp4",
        assetProfileKey: "male_default",
        runtimePackKey: "dentist_runtime",
        runtimePath: DEBUG_MALE_DENTIST_RUNTIME_PATH,
      },
    };
  }

  return {
    id: currentAvatar?.id ?? 0,
    name: currentAvatar?.name ?? "female_20_34 viseme debug",
    age: currentAvatar?.age ?? 28,
    gender: "Female",
    type: "patient",
    personality: currentAvatar?.personality,
    occupation: currentAvatar?.occupation,
    image: currentAvatar?.image,
    videoFiles: {
      baseVideo: currentAvatar?.videoFiles?.baseVideo ?? "/videos/assets/patients/female_20_34/base/idle.mp4",
      image: currentAvatar?.videoFiles?.image ?? "/videos/assets/patients/female_20_34/base/avatar.png",
      dubbingVideo: currentAvatar?.videoFiles?.dubbingVideo ?? "/videos/assets/patients/female_20_34/dubbing/dubbing.mp4",
      assetProfileKey: "female_20_34",
      runtimePackKey: "patient_runtime",
      runtimePath: DEBUG_FEMALE_20_34_RUNTIME_PATH,
    },
  };
}

function resolveDebugPlaybackRate(): number {
  const params = new URLSearchParams(window.location.search);
  const rateParam = Number(params.get("visemeDebugRate"));
  if (Number.isFinite(rateParam) && rateParam >= 0.1 && rateParam <= 1) {
    return rateParam;
  }
  const slowParam = Number(params.get("visemeDebugSlow"));
  if (Number.isFinite(slowParam) && slowParam > 1) {
    return Math.max(0.1, Math.min(1, 1 / slowParam));
  }
  return 1;
}

function shouldLogDebugProfile(profileKey: string): boolean {
  const params = new URLSearchParams(window.location.search);
  const value = params.get("visemeDebugLog");
  if (!value) return profileKey === "generatedTransitions";

  const normalized = value.toLowerCase();
  if (normalized === "0" || normalized === "false" || normalized === "off") return false;
  if (normalized === "1" || normalized === "true" || normalized === "all") return true;

  return normalized
    .split(",")
    .map((item) => item.trim())
    .includes(profileKey.toLowerCase());
}

function resolveInitialDebugText(): string {
  const params = new URLSearchParams(window.location.search);
  return params.get("visemeCaptureText") || TEST_PHRASES[0];
}

function resolveCaptureMode(): boolean {
  const params = new URLSearchParams(window.location.search);
  return params.get("visemeCapture") === "1";
}

function resolveCaptureInitialDelayMs(): number {
  const params = new URLSearchParams(window.location.search);
  const value = Number(params.get("visemeCaptureDelayMs"));
  return Number.isFinite(value) && value >= 0 && value <= 5000 ? value : 0;
}

function resolveCaptureSampleUrl(): string | null {
  const params = new URLSearchParams(window.location.search);
  return params.get("visemeCaptureSampleUrl");
}

function resolveCaptureVisualProfile(): VisemeVisualProfile {
  const params = new URLSearchParams(window.location.search);
  return params.get("visemeCaptureProfile") === "generatedTransitions"
    ? "generatedTransitions"
    : "endingSettle";
}

function buildGeneratedTransitionUrls(runtimePackRoot: string): string[] {
  return GENERATED_TRANSITION_PAIRS.flatMap((pair) =>
    Array.from({ length: GENERATED_TRANSITION_FRAME_MAX_COUNT }, (_, index) => {
      const frameNumber = String(index + 1).padStart(2, "0");
      return `${runtimePackRoot}/transition_${pair}_${frameNumber}.jpg`;
    })
  );
}

async function prewarmGeneratedTransitionAssets(runtimePackRoot: string): Promise<void> {
  const urls = buildGeneratedTransitionUrls(runtimePackRoot);
  await Promise.all(
    urls.map(async (url) => {
      await Promise.all([
        preloadImageInfo(url),
        loadCanvasImage(url).catch(() => null),
      ]);
    })
  );
}

function base64ToObjectUrl(base64: string, contentType: string): string {
  const binary = window.atob(base64);
  const bytes = new Uint8Array(binary.length);
  for (let index = 0; index < binary.length; index += 1) {
    bytes[index] = binary.charCodeAt(index);
  }
  return URL.createObjectURL(new Blob([bytes], { type: contentType }));
}

export default function VisemeDebug() {
  const [debugTarget, setDebugTarget] = useState<DebugAvatarTarget>(() => resolveDebugTarget());
  const [text, setText] = useState(() => resolveInitialDebugText());
  const [audioUrl, setAudioUrl] = useState<string | null>(null);
  const [visemes, setVisemes] = useState<MappedVisemeEvent[]>([]);
  const [isPlaying, setIsPlaying] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);
  const [playKey, setPlayKey] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [canonicalPoseImageUrls, setCanonicalPoseImageUrls] = useState<CanonicalPoseImageUrls | undefined>();
  const [eyePosition, setEyePosition] = useState<EyePosition | undefined>();
  const [debugSnapshots, setDebugSnapshots] = useState<Record<string, VisemeDebugSnapshot | null>>({});
  const playbackClockRef = useRef<VisemePlaybackClock>({
    timeMs: 0,
    durationMs: null,
    status: "idle",
    playbackRate: 1,
  });

  const { data: currentAvatar } = useQuery<Avatar>({
    queryKey: ["/api/avatar/current", "viseme-debug"],
    queryFn: async () => {
      const response = await fetch("/api/avatar/current", {
        credentials: "include",
        cache: "no-store",
      });
      if (!response.ok) throw new Error(await response.text());
      return response.json();
    },
    refetchOnMount: true,
    refetchOnWindowFocus: true,
    staleTime: 0,
    gcTime: 0,
  });

  const debugAvatar = useMemo(() => resolveDebugAvatar(debugTarget, currentAvatar), [currentAvatar, debugTarget]);
  const debugPlaybackRate = useMemo(() => resolveDebugPlaybackRate(), []);
  const captureMode = useMemo(() => resolveCaptureMode(), []);
  const captureInitialDelayMs = useMemo(() => resolveCaptureInitialDelayMs(), []);
  const captureSampleUrl = useMemo(() => resolveCaptureSampleUrl(), []);
  const captureVisualProfile = useMemo(() => resolveCaptureVisualProfile(), []);
  const avatarAssets = useMemo(() => resolveInterviewAvatarAssets(debugAvatar), [debugAvatar]);
  const isDentistDebug = debugAvatar.type === "dentist";
  const canvasEyeMaskUrl = `${avatarAssets.runtimePackRoot}/eye_mask.png`;
  const resolvedEyePosition = useMemo(
    () => eyePosition ?? (isDentistDebug ? DEFAULT_DENTIST_EYE_POSITION : undefined),
    [eyePosition, isDentistDebug]
  );
  const speakingEyeMotion = useMemo(() => {
    const baseMotion = isDentistDebug
      ? buildDentistSpeakingEyeMotion(resolvedEyePosition ?? DEFAULT_DENTIST_EYE_POSITION)
      : PATIENT_SPEAKING_EYE_MOTION;

    return {
      ...baseMotion,
      maskImage: `url(${canvasEyeMaskUrl})`,
      maskMode: "luminance" as const,
      maskPosition: "center",
      maskSize: "100% 100%",
      canvasBlinkMaskUrl: canvasEyeMaskUrl,
    };
  }, [canvasEyeMaskUrl, isDentistDebug, resolvedEyePosition]);
  const jawMotionConfig = useMemo(
    () => ({
      ...(isDentistDebug ? MALE_DENTIST_FRONTBASE_JAW_MOTION : PATIENT_JAW_MOTION),
      maskImage: `url(${avatarAssets.jawMaskUrl})`,
    }),
    [avatarAssets.jawMaskUrl, isDentistDebug]
  );

  useEffect(() => {
    let cancelled = false;
    setCanonicalPoseImageUrls(undefined);
    fetchCanonicalPoseImageUrls(debugAvatar).then((urls) => {
      if (!cancelled) setCanonicalPoseImageUrls(urls);
    });
    fetchEyePosition(debugAvatar).then((position) => {
      if (!cancelled) setEyePosition(position);
    });
    return () => {
      cancelled = true;
    };
  }, [debugAvatar]);

  useEffect(() => {
    return () => {
      if (audioUrl) URL.revokeObjectURL(audioUrl);
    };
  }, [audioUrl]);

  const generateAndPlay = async () => {
    setIsGenerating(true);
    setError(null);
    if (captureMode) {
      (window as any).__VISEME_CAPTURE_STATUS = "generating";
    }
    try {
      if (captureSampleUrl) {
        const sampleResponse = await fetch(captureSampleUrl, { cache: "no-store" });
        if (!sampleResponse.ok) throw new Error(await sampleResponse.text());
        const sample = (await sampleResponse.json()) as {
          text?: string;
          contentType?: string;
          audioPath?: string;
          visemes?: MappedVisemeEvent[];
        };
        const audioUrlValue = new URL(sample.audioPath || "sample.mp3", new URL(captureSampleUrl, window.location.href));
        const audioResponse = await fetch(audioUrlValue.toString(), { cache: "no-store" });
        if (!audioResponse.ok) throw new Error(await audioResponse.text());
        const audioBlob = await audioResponse.blob();
        const nextUrl = URL.createObjectURL(audioBlob);
        if (audioUrl) URL.revokeObjectURL(audioUrl);
        setAudioUrl(nextUrl);
        setVisemes(sample.visemes ?? []);
        await prewarmGeneratedTransitionAssets(avatarAssets.runtimePackRoot);
        if (captureMode) {
          (window as any).__VISEME_CAPTURE_STATUS = "playing";
        }
        playbackClockRef.current = {
          timeMs: 0,
          durationMs: null,
          status: "idle",
          playbackRate: debugPlaybackRate,
        };
        setIsPlaying(true);
        setPlayKey((current) => current + 1);
        return;
      }

      const response = await apiRequest("POST", "/api/generate-viseme-debug-sample", {
        text,
        gender: isDentistDebug ? "Male" : "Female",
      });
      const sample = (await response.json()) as DebugSampleResponse;
      const nextUrl = base64ToObjectUrl(sample.audioBase64, sample.contentType || "audio/mpeg");
      if (audioUrl) URL.revokeObjectURL(audioUrl);
      setAudioUrl(nextUrl);
      setVisemes(sample.visemes ?? []);
      await prewarmGeneratedTransitionAssets(avatarAssets.runtimePackRoot);
      if (captureMode) {
        (window as any).__VISEME_CAPTURE_AUDIO_BASE64 = sample.audioBase64;
        (window as any).__VISEME_CAPTURE_CONTENT_TYPE = sample.contentType || "audio/mpeg";
        (window as any).__VISEME_CAPTURE_VISEMES = sample.visemes ?? [];
        (window as any).__VISEME_CAPTURE_STATUS = "playing";
      }
      playbackClockRef.current = {
        timeMs: 0,
        durationMs: null,
        status: "idle",
        playbackRate: debugPlaybackRate,
      };
      setIsPlaying(true);
      setPlayKey((current) => current + 1);
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : String(caught));
      if (captureMode) {
        (window as any).__VISEME_CAPTURE_STATUS = "error";
        (window as any).__VISEME_CAPTURE_ERROR = caught instanceof Error ? caught.message : String(caught);
      }
    } finally {
      setIsGenerating(false);
    }
  };

  const replay = async () => {
    if (!audioUrl || visemes.length === 0) return;
    setIsGenerating(true);
    await prewarmGeneratedTransitionAssets(avatarAssets.runtimePackRoot);
    playbackClockRef.current = {
      timeMs: 0,
      durationMs: null,
      status: "idle",
      playbackRate: debugPlaybackRate,
    };
    setIsPlaying(true);
    setPlayKey((current) => current + 1);
    setIsGenerating(false);
  };

  const changeDebugTarget = (nextTarget: DebugAvatarTarget) => {
    if (nextTarget === debugTarget) return;
    if (audioUrl) URL.revokeObjectURL(audioUrl);
    setAudioUrl(null);
    setVisemes([]);
    setIsPlaying(false);
    setError(null);
    setDebugTarget(nextTarget);
  };

  useEffect(() => {
    if (!captureMode) return;
    (window as any).__VISEME_CAPTURE_STATUS = (window as any).__VISEME_CAPTURE_STATUS || "idle";
    (window as any).__VISEME_CAPTURE_PLAY = () => {
      void generateAndPlay();
    };
    return () => {
      delete (window as any).__VISEME_CAPTURE_PLAY;
    };
  });

  if (captureMode) {
    return (
      <div className="fixed inset-0 z-[9999] bg-black">
        <AvatarCanvasIdle
          imageUrl={avatarAssets.baseImageUrl}
          eyesHalfBlinkUrl={avatarAssets.eyesHalfBlinkUrl}
          eyesBlinkUrl={avatarAssets.eyesBlinkUrl}
          eyeMaskUrl={canvasEyeMaskUrl}
          suppressBlink={isPlaying}
          eyePosition={resolvedEyePosition}
          internalWidth={DEBUG_CANVAS_WIDTH}
          internalHeight={DEBUG_CANVAS_HEIGHT}
          alt={`${debugAvatar.name} capture`}
        />
        {isPlaying && audioUrl && visemes.length > 0 && (
          <div className="absolute inset-0 z-10">
            <VisemePlayer
              key={`capture-${playKey}`}
              audioUrl={audioUrl}
              visemes={visemes}
              baseImageUrl={avatarAssets.baseImageUrl}
              mouthImageUrl={avatarAssets.mouthImageUrl}
              visemeImageUrls={avatarAssets.visemeImageUrls}
              canonicalPoseImageUrls={canonicalPoseImageUrls}
              debug={shouldLogDebugProfile("generatedTransitions")}
              debugLabel="capture/generatedTransitions"
              debugConsoleLog={shouldLogDebugProfile("generatedTransitions")}
              runtimePackRoot={avatarAssets.runtimePackRoot}
              eyeMotion={speakingEyeMotion}
              jawMotion={jawMotionConfig}
              initialPlaybackDelayMs={captureInitialDelayMs}
              playbackRateOverride={debugPlaybackRate}
              visualProfile={captureVisualProfile}
              renderMode="canvas"
              canvasEyePosition={resolvedEyePosition}
              canvasInternalWidth={DEBUG_CANVAS_WIDTH}
              canvasInternalHeight={DEBUG_CANVAS_HEIGHT}
              onEnded={() => {
                setIsPlaying(false);
                (window as any).__VISEME_CAPTURE_STATUS = "ended";
              }}
            />
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="relative left-1/2 right-1/2 -ml-[50vw] -mr-[50vw] w-screen space-y-4 px-4">
      <div>
        <h1 className="text-xl font-semibold text-gray-900">Viseme Debug</h1>
        <p className="mt-1 text-sm text-gray-600">
          固定文で同じ avatar runtime のCanvas口形を確認します。debug overlay は常時表示されます。
        </p>
      </div>

      <section className="rounded border bg-white p-3">
        <div className="mb-3 flex flex-wrap gap-2">
          {DEBUG_AVATAR_TARGET_OPTIONS.map((option) => (
            <Button
              key={option.key}
              type="button"
              variant={debugTarget === option.key ? "default" : "outline"}
              size="sm"
              onClick={() => changeDebugTarget(option.key)}
            >
              {option.label}
            </Button>
          ))}
        </div>

        <div className="grid gap-3 lg:grid-cols-[minmax(0,1fr)_auto] lg:items-end">
          <div className="space-y-2">
            <label className="text-sm font-medium text-gray-800" htmlFor="viseme-debug-text">
              テスト文
            </label>
            <Textarea
              id="viseme-debug-text"
              value={text}
              onChange={(event) => setText(event.target.value)}
              rows={2}
            />
          </div>

          <div className="flex flex-wrap gap-2 lg:justify-end">
            <Button
              type="button"
              className="min-w-36"
              onClick={generateAndPlay}
              disabled={isGenerating || !text.trim()}
            >
              {isGenerating ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Play className="mr-2 h-4 w-4" />}
              生成して再生
            </Button>
            <Button
              type="button"
              variant="outline"
              className="min-w-28"
              onClick={replay}
              disabled={!audioUrl || visemes.length === 0}
            >
              <RotateCcw className="mr-2 h-4 w-4" />
              再再生
            </Button>
          </div>
        </div>

        {error && <div className="mt-3 rounded bg-red-50 p-2 text-xs text-red-700">{error}</div>}
      </section>

      <div className="grid gap-4">
        <section className="rounded border bg-white p-2">
          <div className="grid gap-3 lg:grid-cols-2">
            {VISUAL_PROFILE_OPTIONS.map((profile, index) => {
              const snapshot = debugSnapshots[profile.key] ?? null;
              return (
              <div key={profile.key} className="space-y-2">
                <div className="flex h-12 items-start justify-between gap-2">
                  <div className="min-w-0 flex-1">
                    <div className="text-sm font-semibold text-gray-900">{profile.label}</div>
                    <div className="line-clamp-2 text-xs leading-tight text-gray-500">{profile.description}</div>
                  </div>
                  {index > 0 && (
                    <span className="shrink-0 rounded border px-2 py-0.5 text-xs text-gray-600">sync</span>
                  )}
                </div>
                <div className="relative aspect-video overflow-hidden rounded bg-black">
                  {debugAvatar && (
                    <>
                      <AvatarCanvasIdle
                        imageUrl={avatarAssets.baseImageUrl}
                        eyesHalfBlinkUrl={avatarAssets.eyesHalfBlinkUrl}
                        eyesBlinkUrl={avatarAssets.eyesBlinkUrl}
                        eyeMaskUrl={canvasEyeMaskUrl}
                        suppressBlink={isPlaying}
                        eyePosition={resolvedEyePosition}
                        internalWidth={DEBUG_CANVAS_WIDTH}
                        internalHeight={DEBUG_CANVAS_HEIGHT}
                        alt={`${debugAvatar.name} ${profile.label}`}
                      />
                      {isPlaying && audioUrl && visemes.length > 0 && (
                        <div className="absolute inset-0 z-10">
                          <VisemePlayer
                            key={`${playKey}-${profile.key}`}
                            audioUrl={audioUrl}
                            visemes={visemes}
                            baseImageUrl={avatarAssets.baseImageUrl}
                            mouthImageUrl={avatarAssets.mouthImageUrl}
                            visemeImageUrls={avatarAssets.visemeImageUrls}
                            canonicalPoseImageUrls={canonicalPoseImageUrls}
                            debug
                            debugLabel={`${profile.key}/${profile.label}`}
                            debugConsoleLog={shouldLogDebugProfile(profile.key)}
                            inlineDebugOverlay={false}
                            onDebugSnapshotChange={(next) =>
                              setDebugSnapshots((prev) => ({ ...prev, [profile.key]: next }))
                            }
                            runtimePackRoot={avatarAssets.runtimePackRoot}
                            eyeMotion={speakingEyeMotion}
                            jawMotion={jawMotionConfig}
                            playbackRateOverride={debugPlaybackRate}
                            playbackMode={index === 0 ? "audio" : "clock"}
                            playbackClockRef={playbackClockRef}
                            visualProfile={profile.visualProfile}
                            renderMode="canvas"
                            canvasEyePosition={resolvedEyePosition}
                            canvasInternalWidth={DEBUG_CANVAS_WIDTH}
                            canvasInternalHeight={DEBUG_CANVAS_HEIGHT}
                            onEnded={index === 0 ? () => setIsPlaying(false) : undefined}
                          />
                        </div>
                      )}
                    </>
                  )}
                </div>
                {snapshot && (
                  <div className="rounded border border-gray-200 bg-gray-50 px-2 py-1.5 text-left font-mono text-[10px] leading-tight text-gray-700">
                    <div className="break-all">runtime: {snapshot.runtimePackRoot ?? "-"}</div>
                    <div>azure: {snapshot.azureVisemeId ?? "-"} / pose: {snapshot.dentalPose ?? "-"}</div>
                    <div>target: {snapshot.targetAssetSource} / {snapshot.targetImage}</div>
                    <div>legacy: {snapshot.legacyViseme ?? "-"} / frame: {snapshot.assetSource} / {snapshot.displayImage}</div>
                    <div>asset: {snapshot.assetSource} / transition: canvas</div>
                    <div>
                      trans: {snapshot.transitionLoadedCount ?? "-"} loaded / preload: {String(snapshot.transitionPreloadComplete ?? false)}
                      {" "} / canvas: {String(snapshot.canvasImagesReady ?? false)}
                    </div>
                    <div>
                      queue: {snapshot.displayQueueLength ?? "-"} / target: {snapshot.activeTransitionTarget ?? "-"}
                      {" "} / min: {snapshot.minDisplayFrameMs ?? "-"}ms
                    </div>
                    <div>
                      canvasFps: {snapshot.canvasFps ?? "-"} / renders: {snapshot.canvasRenderCount ?? "-"}
                      {" "} / dt: {snapshot.canvasRenderDeltaMs ?? "-"}ms
                    </div>
                    <div>jaw: {snapshot.jawOpen} / y: {snapshot.jawTranslateY}% / scaleY: {snapshot.jawScaleY}</div>
                    <div>rate: {snapshot.playbackRate} / status: {snapshot.status ?? "-"}</div>
                    <div className="break-all">url: {snapshot.assetUrl}</div>
                  </div>
                )}
              </div>
              );
            })}
          </div>
        </section>

        <aside className="space-y-3 rounded border bg-white p-3">
          <div className="grid gap-2 md:grid-cols-2 xl:grid-cols-3">
            {TEST_PHRASES.map((phrase) => (
              <button
                key={phrase}
                type="button"
                className="rounded border px-2 py-1 text-left text-xs hover:bg-gray-50"
                onClick={() => setText(phrase)}
              >
                {phrase}
              </button>
            ))}
          </div>

          <div className="break-all rounded bg-gray-50 p-2 font-mono text-xs text-gray-700">
            runtime: {avatarAssets.runtimePackRoot}
            <br />
            avatar: {debugTarget}
            <br />
            playbackRate: {debugPlaybackRate}
          </div>
          <div className="text-xs text-gray-600">visemes: {visemes.length}</div>
        </aside>
      </div>
    </div>
  );
}
